import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { 
  Beer, 
  LayoutDashboard, 
  FileText, 
  Upload, 
  History, 
  Users, 
  LogOut,
  User,
  Menu,
  ChevronDown
} from 'lucide-react';
import { useState } from 'react';

const Layout = ({ children }) => {
  const { user, logout, canEdit, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      office: 'Büro',
      service: 'Service',
      kitchen: 'Küche'
    };
    return labels[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-burgundy text-white',
      office: 'bg-gold text-charcoal-900',
      service: 'bg-emerald-600 text-white',
      kitchen: 'bg-orange-500 text-white'
    };
    return colors[role] || 'bg-stone-500 text-white';
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'office', 'service', 'kitchen'] },
    { path: '/sheets', label: 'Function Sheets', icon: FileText, roles: ['admin', 'office', 'service', 'kitchen'] },
    { path: '/upload', label: 'PDF Upload', icon: Upload, roles: ['admin', 'office'] },
    { path: '/history', label: 'Historie', icon: History, roles: ['admin', 'office'] },
    { path: '/users', label: 'Benutzer', icon: Users, roles: ['admin'] },
  ];

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <Beer className="h-8 w-8 text-gold" />
              <div className="hidden sm:block">
                <h1 className="font-serif text-xl font-bold text-burgundy leading-tight">Mautkeller</h1>
                <p className="text-[10px] tracking-widest text-charcoal-500 uppercase -mt-1">VA-Facts</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {filteredNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant={isActive ? 'default' : 'ghost'}
                      className={`gap-2 ${isActive 
                        ? 'bg-burgundy hover:bg-burgundy-800 text-white' 
                        : 'text-charcoal-600 hover:text-burgundy hover:bg-stone-100'
                      }`}
                      data-testid={`nav-${item.path.slice(1)}`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="gap-2 text-charcoal-600 hover:text-burgundy"
                    data-testid="user-menu-trigger"
                  >
                    <div className="h-8 w-8 rounded-full bg-burgundy-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-burgundy" />
                    </div>
                    <span className="hidden sm:inline-block max-w-[100px] truncate">
                      {user?.name}
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-semibold">{user?.name}</span>
                      <span className="text-xs text-charcoal-500">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-default">
                    <Badge className={`${getRoleBadgeColor(user?.role)} text-xs`}>
                      {getRoleLabel(user?.role)}
                    </Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden py-4 border-t border-stone-200 animate-fade-in">
              <div className="flex flex-col gap-2">
                {filteredNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <Link 
                      key={item.path} 
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Button
                        variant={isActive ? 'default' : 'ghost'}
                        className={`w-full justify-start gap-2 ${isActive 
                          ? 'bg-burgundy hover:bg-burgundy-800 text-white' 
                          : 'text-charcoal-600'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-stone-200 py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-charcoal-500">
            <div className="flex items-center gap-2">
              <Beer className="h-4 w-4 text-gold" />
              <span>Tucher Mautkeller Brauhaus Nürnberg</span>
            </div>
            <div>
              VA-Facts Manager © {new Date().getFullYear()}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
